import { createColumnHelper } from '@tanstack/react-table';

import { AlertRuleAttachments as AlertRuleAttachmentsData } from '@/react/portainer/observability/types';
import { AttachmentsDatatableDescription } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/AttachmentsDatatableDescription';

import { Badge } from '@@/Badge';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Widget } from '@@/Widget';
import { Datatable } from '@@/datatables';
import { Link } from '@@/Link';

type AlertRuleEnvironment =
  AlertRuleAttachmentsData[number]['environments'][number];
const columnHelper = createColumnHelper<AlertRuleEnvironment>();
const alertRuleEnvColumns = [
  columnHelper.accessor('name', {
    header: 'Environment name',
    id: 'name',
    meta: { width: '250px' },
    enableSorting: false,
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    id: 'status',
    meta: { width: '150px' },
    enableSorting: false,
    cell: ({ getValue }) => {
      const status = getValue();
      switch (status.toLowerCase()) {
        case 'active':
          return <Badge type="success">{status}</Badge>;
        case 'warning':
          return <Badge type="warn">{status}</Badge>;
        case 'failed':
          return <Badge type="danger">{status}</Badge>;
        default:
          return <Badge type="muted">{status || '-'}</Badge>;
      }
    },
  }),
  columnHelper.accessor('message', {
    header: 'Message',
    id: 'message',
    meta: { width: 'auto', minWidth: '200px' },
    enableSorting: false,
    cell: ({ getValue }) => (
      <span className="text-muted whitespace-normal break-words text-sm">
        {getValue() || '-'}
      </span>
    ),
  }),
];

interface AlertRuleGroupDataTableProps {
  attachmentGroup: AlertRuleAttachmentsData[number];
  alertRuleId: number;
  groupSize?: number;
}

export function AlertRuleGroupDataTable({
  attachmentGroup,
  alertRuleId,
  groupSize,
}: AlertRuleGroupDataTableProps) {
  const storageKey = `alert-rule-attachments-group-${alertRuleId}-${attachmentGroup.endpointGroupId}`;
  const settingsStore = createPersistedStore(storageKey, 'name');
  const tableState = useTableState(settingsStore, storageKey);

  return (
    <Widget>
      <Datatable
        noWidget
        title={
          <Link
            to="portainer.groups.group"
            params={{ id: attachmentGroup.endpointGroupId }}
            data-cy={`environment-group-link-${attachmentGroup.endpointGroupName}`}
          >
            {attachmentGroup.endpointGroupName}
          </Link>
        }
        description={
          <AttachmentsDatatableDescription
            size={groupSize ?? attachmentGroup.environments.length}
          />
        }
        dataset={attachmentGroup.environments}
        columns={alertRuleEnvColumns}
        settingsManager={tableState}
        disableSelect
        emptyContentLabel="No environments in this group"
        data-cy={`alert-rule-attachments-group-${attachmentGroup.endpointGroupId}`}
        includeSearch={false}
      />
    </Widget>
  );
}
